import { useMemo } from "react";
import { AVAILABLE_DAYS, ISSUES } from "@/data/issues";
import type { IssueStatus, IssueView } from "@/types/lex";
import { useAllMastery } from "./useMastery";
import { dueCountByDay, useSrsSnapshot } from "./useSrs";

function deriveStatus(opts: {
  available: boolean;
  total: number;
  known: number;
  due: number;
}): IssueStatus {
  if (!opts.available) return "locked";
  // Cards waiting on the schedule outrank everything else — that's the day the
  // learner should open next.
  if (opts.due > 0) return "due-review";
  if (opts.known === opts.total) return "done";
  // Available but never opened also lands here, at 0% — the card renderer
  // suppresses the amber tint when nothing has been touched.
  return "in-progress";
}

/**
 * Derive the per-day view rows (mastered count, words seen, cards due, status)
 * by combining the static catalog with the mastery ledger and the schedule.
 * Recomputes whenever either store changes.
 */
export function useIssueViews(): readonly IssueView[] {
  const mastery = useAllMastery();
  const srs = useSrsSnapshot();

  return useMemo(() => {
    const due = dueCountByDay(Date.now());
    return ISSUES.map<IssueView>((meta) => {
      const counts = mastery.countByDay(meta.day);
      const dueCount = due.get(meta.day) ?? 0;
      return {
        ...meta,
        progress: counts.known,
        touched: counts.touched,
        due: dueCount,
        status: deriveStatus({
          available: AVAILABLE_DAYS.has(meta.day),
          total: meta.total,
          known: counts.known,
          due: dueCount,
        }),
      };
    });
    // `srs` is the store snapshot: a new object identity on every schedule
    // write, which is exactly when the due counts need recomputing.
  }, [mastery, srs]);
}
